// Postman-style URL editing: the raw URL is the source of truth and the params
// table is a view of its query string. Values are kept exactly as typed (no
// decoding or re-encoding), so what you see is what the server sends.
//
// Parsing is done by hand rather than with URL/URLSearchParams, which would
// re-encode values and mangle {{placeholders}}.

export const splitUrl = (raw = "") => {
    const hashAt = raw.indexOf("#");
    const withoutHash = hashAt >= 0 ? raw.slice(0, hashAt) : raw;
    const queryAt = withoutHash.indexOf("?");
    return {
        base: queryAt >= 0 ? withoutHash.slice(0, queryAt) : withoutHash,
        query: queryAt >= 0 ? withoutHash.slice(queryAt + 1) : null,
        hash: hashAt >= 0 ? raw.slice(hashAt) : "",
    };
};

/**
 * Query string → rows. Empty segments ("a=1&&b=2") are skipped.
 *
 * @returns {Array<{ key: string, value: string, hasEquals: boolean }>}
 */
export const parseQueryRows = (raw) => {
    const { query } = splitUrl(raw);
    if (!query) return [];
    return query
        .split("&")
        .filter((part) => part !== "")
        .map((part) => {
            const at = part.indexOf("=");
            return at >= 0
                ? { key: part.slice(0, at), value: part.slice(at + 1), hasEquals: true }
                : { key: part, value: "", hasEquals: false };
        });
};

const rowText = (row) => (row.hasEquals || row.value !== "" ? `${row.key}=${row.value}` : row.key);

/**
 * Rows → URL, keeping the base URL and fragment as they were.
 */
export const withQueryRows = (raw, rows) => {
    const { base, hash } = splitUrl(raw);
    const query = rows.filter((row) => row.key !== "" || row.value !== "").map(rowText).join("&");
    return `${base}${query ? `?${query}` : ""}${hash}`;
};

// Characters that would change the query's structure are encoded as they're
// typed into the table; everything else stays as typed.
export const encodeCell = (text, { name = false } = {}) =>
    String(text ?? "").replace(name ? /[&#=\s]/g : /[&#\s]/g, (ch) => encodeURIComponent(ch));
